import { create } from "zustand";
import type { DakeEnumOption } from "@/data-contracts";
import { OPR_SESSION_TOKEN } from "@/constants";
import apis from "@/services";

/** 后端 /admin/enum/options 当前支持的枚举键 */
export const ENUM_OPTION_KEYS = ["productVisibility", "sampleGmv", "sampleFans"] as const;

export type EnumOptionKey = (typeof ENUM_OPTION_KEYS)[number];

export type EnumOptionItem = {
  value: number;
  label: string;
  description?: string;
  minValue?: string;
  maxValue?: string;
};

type EnumOptionsState = {
  enums: Partial<Record<EnumOptionKey, EnumOptionItem[]>>;
  loading: boolean;
  loaded: boolean;
  fetchEnumOptions: () => Promise<void>;
};

function normalizeOptions(list?: DakeEnumOption[]): EnumOptionItem[] {
  return (list ?? []).filter((item): item is EnumOptionItem => item.value != null && !!item.label);
}

export const useEnumOptionsStore = create<EnumOptionsState>()((set, get) => ({
  enums: {},
  loading: false,
  loaded: false,
  fetchEnumOptions: async () => {
    if (!window.localStorage.getItem(OPR_SESSION_TOKEN)) return;
    if (get().loading) return;
    const hasData = ENUM_OPTION_KEYS.some((key) => (get().enums[key]?.length ?? 0) > 0);
    if (hasData) return;

    set({ loading: true });
    try {
      const { data } = await apis.app.getEnumOptions({});
      const next: Partial<Record<EnumOptionKey, EnumOptionItem[]>> = {};
      for (const key of ENUM_OPTION_KEYS) {
        next[key] = normalizeOptions(data?.enums?.[key]);
      }
      set({ enums: next, loaded: true });
    } finally {
      set({ loading: false });
    }
  },
}));

/** 非 React 场景预取/刷新枚举 */
export function fetchEnumOptions() {
  return useEnumOptionsStore.getState().fetchEnumOptions();
}

export function getEnumOptions(key: EnumOptionKey): EnumOptionItem[] {
  return useEnumOptionsStore.getState().enums[key] ?? [];
}

export function getEnumOptionLabel(key: EnumOptionKey, value?: number) {
  if (value == null) return "-";
  return getEnumOptions(key).find((item) => item.value === value)?.label ?? "-";
}
